<template>
  <v-container class="contact-page" fluid>
    <v-popup-loading :isLoading="isPageLoading" />
    <v-row>
      <v-col>
        <v-breadcrumbs :items="breadcrumbs" divider=">" class="px-0 pb-0"></v-breadcrumbs>
      </v-col>
    </v-row>

    <div class="head-wrap mb-4">
      <div>
        <h1 class="headline mb-1">{{ isEditMode ? 'Edit Patient Room' : 'Create Patient Room' }} <span class="thai">ห้องพักผู้ป่วย</span></h1>
        <p class="subhead mb-0">Fields marked required should be completed before saving.</p>
      </div>
      <div class="top-actions">
        <v-btn outlined class="mr-2" @click="onCancel">Cancel</v-btn>
        <v-btn v-if="canSave" color="primary" :loading="isSaving" :disabled="!isFormValid" @click="onSave">Save</v-btn>
      </div>
    </div>

    <v-alert v-if="saveError" type="error" dense outlined class="mb-4">{{ saveError }}</v-alert>

    <v-card class="form-card">
      <div class="form-card-head">Basic Information</div>
      <v-card-text class="pt-4">
        <v-row>
            <v-col cols="12" md="6"><label>ROOM NAME <span class="lang">TH</span> <span class="required">*</span></label><v-text-field dense outlined hide-details v-model="form.name_th" placeholder="เช่น พรีเมียร์ สวีท"/></v-col>
            <v-col cols="12" md="6"><label>ROOM NAME <span class="lang">EN</span> <span class="required">*</span></label><v-text-field dense outlined hide-details v-model="form.name_en" placeholder="e.g. Premier Suite"/></v-col>
            <v-col cols="12" md="6"><label>ROOM DESCRIPTION <span class="lang">TH</span></label><v-textarea dense outlined hide-details rows="3" v-model="form.description_th" placeholder="รายละเอียดห้องพัก..."/></v-col>
            <v-col cols="12" md="6"><label>ROOM DESCRIPTION <span class="lang">EN</span></label><v-textarea dense outlined hide-details rows="3" v-model="form.description_en" placeholder="Room description..."/></v-col>
            <v-col cols="12" md="6">
              <label>ROOM CAPACITY <span class="required">*</span></label>
              <v-text-field type="number" min="1" dense outlined hide-details v-model.number="form.capacity" @input="clampNonNegative('capacity')" placeholder="e.g. 2"/>
              <div class="hint">Number of patients / beds per room</div>
            </v-col>
            <v-col cols="12" md="6"><label>ROOM SIZE <span class="required">*</span></label><v-text-field dense outlined hide-details v-model="form.room_size" placeholder="e.g. 35 sq.m."/></v-col>

            <v-col cols="12" md="6"><label>ROOM TYPE <span class="required">*</span></label><v-text-field dense outlined hide-details v-model="form.room_type" placeholder="e.g. VIP Room"/></v-col>
            <v-col v-if="isSuperAdmin" cols="12" md="6">
              <label>BRANCH <span class="required">*</span></label>
              <v-select dense outlined hide-details :items="orgOptions" item-text="text" item-value="value" v-model="form.org_codename" placeholder="Select Branch"/>
            </v-col>
          </v-row>
      </v-card-text>
    </v-card>

    <v-card class="form-card">
      <div class="form-card-head">Itemised Pricing <div class="head-sub">Total price is calculated automatically from the fields below.</div></div>
      <v-card-text class="pt-4">
        <div class="pricing-row">
          <div class="price-label"><div>Room Charge</div><small>ค่าห้องพัก</small></div>
          <div class="price-input"><v-text-field type="number" min="0" dense outlined hide-details suffix="THB" v-model.number="form.room_charge" @input="clampNonNegative('room_charge')"/></div>
        </div>
        <div class="pricing-row">
          <div class="price-label"><div>Food Charge</div><small>ค่าอาหาร</small></div>
          <div class="price-input"><v-text-field type="number" min="0" dense outlined hide-details suffix="THB" v-model.number="form.food_charge" @input="clampNonNegative('food_charge')"/></div>
        </div>
        <div class="pricing-row">
          <div class="price-label"><div>Nursing Charge</div><small>ค่าบริการทางการพยาบาล</small></div>
          <div class="price-input"><v-text-field type="number" min="0" dense outlined hide-details suffix="THB" v-model.number="form.nursing_charge" @input="clampNonNegative('nursing_charge')"/></div>
        </div>
        <div class="pricing-row">
          <div class="price-label"><div>Hospital Service Charge</div><small>ค่าบริการโรงพยาบาล</small></div>
          <div class="price-input"><v-text-field type="number" min="0" dense outlined hide-details suffix="THB" v-model.number="form.service_charge" @input="clampNonNegative('service_charge')"/></div>
        </div>
        <div class="total-row">
          <span>Total Price</span>
          <strong>฿ {{ totalPrice.toLocaleString() }}</strong>
        </div>
      </v-card-text>
    </v-card>

    <v-card class="form-card">
      <div class="form-card-head">Amenities</div>
      <v-card-text class="pt-4">
        <div class="amenity-grid">
          <label v-for="item in amenityOptions" :key="item" class="amenity-item">
            <input type="checkbox" :value="item" v-model="selectedAmenities" />
            <span>{{ item }}</span>
          </label>
        </div>
      </v-card-text>
    </v-card>

    <v-card class="form-card">
      <div class="form-card-head">Media Uploads <div class="head-sub">Files are uploaded and served from OBS cloud storage.</div></div>
      <v-card-text class="pt-4">
        <label>COVER IMAGE</label>
        <input
          ref="coverInput"
          type="file"
          accept="image/*"
          class="d-none"
          @change="onCoverFileChange"
        />
        <div
          class="upload-box mb-3"
          @click="openCoverPicker"
          @dragover.prevent
          @drop.prevent="onCoverDrop"
        >
          <v-icon class="upload-icon" size="26">mdi-cloud-upload-outline</v-icon>
          Drop your cover image here or <b>Browse</b><br/>Single image for room card preview thumbnail — <b>Upload to OBS</b> Max 5 MB
        </div>
        <div v-if="coverPreview" class="cover-preview mb-4">
          <img :src="coverPreview" alt="cover" />
          <button type="button" class="thumb-remove" @click="removeCover">×</button>
        </div>

        <label>GALLERY CAROUSEL</label>
        <input
          ref="galleryInput"
          type="file"
          accept="image/*"
          multiple
          class="d-none"
          @change="onGalleryFileChange"
        />
        <div
          class="upload-box"
          @click="openGalleryPicker"
          @dragover.prevent
          @drop.prevent="onGalleryDrop"
        >
          <v-icon class="upload-icon" size="26">mdi-cloud-upload-outline</v-icon>
          Drop up to 5 gallery photos here or <b>Browse</b><br/><b>Upload to OBS</b>, Max 5 MB per photo
        </div>
        <div class="gallery-row">
          <div v-for="(img, idx) in galleryImages" :key="img.url + idx" class="gallery-thumb has-image">
            <img :src="img.url" :alt="img.name || `photo-${idx + 1}`" />
            <button type="button" class="thumb-remove" @click="removeGalleryImage(idx)">×</button>
          </div>
          <div v-if="galleryImages.length > 0 && galleryImages.length < 5" class="gallery-thumb add" @click.stop="openGalleryPicker">+</div>
        </div>
        <div v-if="uploadError" class="upload-error mt-2">{{ uploadError }}</div>

      </v-card-text>
    </v-card>

    <v-card class="form-card">
      <div class="form-card-head">Billing Conditions & Payment Terms</div>
      <v-card-text class="pt-4">
          <v-row>
            <v-col cols="12" md="6"><label>Billing Conditions (TH)</label><v-textarea dense outlined hide-details rows="3" v-model="form.billing_conditions_th"/></v-col>
            <v-col cols="12" md="6"><label>Billing Conditions (EN)</label><v-textarea dense outlined hide-details rows="3" v-model="form.billing_conditions_en"/></v-col>
          </v-row>
      </v-card-text>
    </v-card>

    <v-card class="form-card actions-card">
      <v-card-actions class="modal-foot">
        <v-spacer />
        <v-btn outlined @click="onCancel">Cancel</v-btn>
        <v-btn v-if="canSave" color="primary" :loading="isSaving" :disabled="!isFormValid" @click="onSave">Save</v-btn>
      </v-card-actions>
    </v-card>
  </v-container>
</template>

<script>
import {mapActions, mapState} from 'vuex'
import {hasPermision} from '@/helper/helper'
import PopupLoading from '@/components/PopupLoading'

const defaultForm = () => ({ id: null, org_codename: '', room_type: '', name_th: '', name_en: '', room_size: '', capacity: 1, cover_image: '', images: [], amenities: [], description_th: '', description_en: '', billing_conditions_th: '', billing_conditions_en: '', room_charge: 0, food_charge: 0, nursing_charge: 0, service_charge: 0 })
const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024 //5 MB
const ROOM_UPLOAD_FOLDER = 'patient-services/rooms'

export default {
  components: {
    'v-popup-loading': PopupLoading
  },
  data() {
    return {
      form: defaultForm(),
      coverPreview: '',
      galleryImages: [],
      selectedAmenities: [],
      amenityOptions: ['Smart TV', 'Wi-Fi', 'Microwave', 'Refrigerator', 'Sofa Bed', 'Nurse Call System'],
      saveError: '',
      uploadError: '',
      initialLoading: false
    }
  },
  computed: {
    ...mapState('patientRooms', ['list', 'isSaving', 'orgOptions']),
    ...mapState('auth', ['user']),
    isPageLoading() { return this.initialLoading },
    isEditMode() { return !!this.$route.params.id },
    isSuperAdmin() {
      const currentUser = this.user || {}
      return !!currentUser.is_superuser
    },
    canSave() { return this.isEditMode ? hasPermision('CanEditPatientRoom') : hasPermision('CanAddPatientRoom') },
    isFormValid() { return !!(this.form.org_codename && this.form.room_type && this.form.name_th && this.form.name_en) },
    totalPrice() {
      return Number(this.form.room_charge || 0) + Number(this.form.food_charge || 0) + Number(this.form.nursing_charge || 0) + Number(this.form.service_charge || 0)
    },
    breadcrumbs() {
      return [
        {text: 'Dashboard', exact: true, href: '/'},
        {text: 'Patient Services', disabled: true, href: '/patient-services/rooms'},
        {text: 'Patient Rooms', disabled: true, href: '/patient-services/rooms'},
        // {text: this.isEditMode ? 'Edit' : 'Create', disabled: true, href: '#'}
      ]
    }
  },
  async created() {
    this.initialLoading = true
    try {
      this.setResource('patient-rooms')
      await this.fetchOrgOptions()
      await this.fetch()

      if (this.isEditMode) {
        const target = this.list.find(item => String(item.id) === String(this.$route.params.id))
        if (target) {
          this.form = {...defaultForm(), ...target}
          this.coverPreview = target.cover_image || ''
          this.galleryImages = Array.isArray(target.images) ? target.images.map((url, index) => ({url, name: `photo-${index + 1}`})) : []
          this.selectedAmenities = Array.isArray(target.amenities) ? target.amenities : []
        }
      } else {
        const userBranch = this.getUserOrgCode() || this.orgOptions[0]?.value || ''
        this.form.org_codename = userBranch
      }
    } finally {
      this.initialLoading = false
    }
  },
  methods: {
    ...mapActions('patientRooms', ['setResource', 'fetch', 'save', 'fetchOrgOptions', 'upload']),
    getUserOrgCode() {
      const currentUser = this.user || {}
      return currentUser.org_code || currentUser.org_codename || currentUser.org || ''
    },
    clampNonNegative(field) {
      const value = Number(this.form[field])
      if (!Number.isFinite(value) || value < 0) {
        this.form[field] = 0
      }
    },
    fileToDataUrl(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result)
        reader.onerror = reject
        reader.readAsDataURL(file)
      })
    },
    async uploadImage(file, namePrefix) {
      const base64 = await this.fileToDataUrl(file)
      const uploadName = `${namePrefix}_${Date.now()}`
      const imageUrl = await this.upload({
        name: uploadName,
        base64,
        folder: ROOM_UPLOAD_FOLDER
      })
      if (!imageUrl) {
        this.uploadError = 'Upload failed. Please try again.'
      }
      return imageUrl
    },
    validateImageFile(file) {
      if (!file || !file.type.startsWith('image/')) {
        this.uploadError = 'Please select an image file.'
        return false
      }
      if (file.size > MAX_IMAGE_SIZE_BYTES) {
        this.uploadError = 'Max 5 MB per photo.'
        return false
      }
      return true
    },
    openCoverPicker() {
      this.$refs.coverInput && this.$refs.coverInput.click()
    },
    openGalleryPicker() {
      if (this.galleryImages.length >= 5) {
        this.uploadError = 'You can upload up to 5 gallery images.'
        return
      }
      this.$refs.galleryInput && this.$refs.galleryInput.click()
    },
    async onCoverFileChange(event) {
      const file = event.target.files && event.target.files[0]
      if (!file || !this.validateImageFile(file)) return

      if (this.coverPreview || this.form.cover_image) {
        const shouldOverride = window.confirm('A cover image already exists. Do you want to replace it?')
        if (!shouldOverride) {
          event.target.value = null
          return
        }
      }

      this.uploadError = ''
      const imageUrl = await this.uploadImage(file, 'patient_room_cover')
      if (!imageUrl) return
      this.coverPreview = imageUrl
      this.form.cover_image = imageUrl
      event.target.value = null
    },
    async onGalleryFileChange(event) {
      await this.processGalleryFiles(Array.from(event.target.files || []))
      event.target.value = null
    },
    async onCoverDrop(event) {
      const file = event.dataTransfer.files && event.dataTransfer.files[0]
      if (!file || !this.validateImageFile(file)) return

      if (this.coverPreview || this.form.cover_image) {
        const shouldOverride = window.confirm('A cover image already exists. Do you want to replace it?')
        if (!shouldOverride) {
          return
        }
      }

      this.uploadError = ''
      const imageUrl = await this.uploadImage(file, 'patient_room_cover')
      if (!imageUrl) return
      this.coverPreview = imageUrl
      this.form.cover_image = imageUrl
    },
    async onGalleryDrop(event) {
      await this.processGalleryFiles(Array.from(event.dataTransfer.files || []))
    },
    async processGalleryFiles(files) {
      if (!files.length) return
      this.uploadError = ''
      const available = 5 - this.galleryImages.length
      const selected = files.slice(0, available)
      if (files.length > available) {
        this.uploadError = 'Only first 5 images are accepted.'
      }

      for (const file of selected) {
        if (!this.validateImageFile(file)) continue
        const imageUrl = await this.uploadImage(file, 'patient_room_gallery')
        if (!imageUrl) continue
        this.galleryImages.push({url: imageUrl, name: file.name})
      }
    },
    removeCover() {
      this.coverPreview = ''
      this.form.cover_image = ''
    },
    removeGalleryImage(index) {
      this.galleryImages.splice(index, 1)
    },
    onCancel() {
      this.$router.push({name: 'patient-room-list'})
    },
    async onSave() {
      const payload = {
        ...this.form,
        cover_image: this.form.cover_image,
        images: this.galleryImages.map(item => item.url),
        amenities: this.selectedAmenities
      }
      const result = await this.$store.dispatch('patientRooms/save', payload)
      if (!result.ok) {
        this.saveError = result.message
        return
      }
      this.$router.push({name: 'patient-room-list'})
    }
  }
}
</script>

<style scoped>
.contact-page { background: #f7f9fc; min-height: calc(100vh - 120px); }
.head-wrap { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; }
.headline { font-size: 30px !important; color: #0f1f3b; }
.thai { color: #7c8aa5; font-size: 18px; }
.subhead { color: #6d7f9e; font-size: 14px; }
.top-actions { display: flex; align-items: center; }
.form-card { border: 1px solid #e5ebf6; border-radius: 12px; margin-bottom: 14px; background: #fff; overflow: hidden; }
.form-card-head { padding: 12px 14px; border-bottom: 1px solid #edf2fb; font-size: 18px; font-weight: 700; color: #0f1f3b; background: #fff; }
.head-sub { font-size: 12px; color: #7586a8; font-weight: 500; margin-top: 4px; }
.modal-foot { border-top: 1px solid #edf2fb; padding: 10px 14px; }
.pricing-row { display: grid; grid-template-columns: 1fr 320px; gap: 16px; align-items: center; padding: 10px 0; border-bottom: 1px solid #eef2fa; }
.pricing-row:last-of-type { border-bottom: none; }
.price-label div { font-weight: 700; color: #0f1f3b; }
.price-label small { color: #7586a8; font-size: 12px; }
.total-row { margin-top: 8px; border-radius: 8px; background: #f1f6ff; color: #1d468e; padding: 14px; display: flex; justify-content: space-between; align-items: center; font-weight: 700; }
.amenity-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
.amenity-item { border: 1px solid #e5ebf6; border-radius: 10px; padding: 10px 12px; display: flex; gap: 10px; align-items: center; font-weight: 600; color: #243a63; }
.upload-box { border: 1.5px dashed #d9e5f8; border-radius: 10px; color: #7a8cad; padding: 22px; text-align: center; font-size: 14px; }
.upload-icon { display: block; margin: 0 auto 8px; color: #9ab0d6; }
.gallery-row { margin-top: 12px; display: flex; gap: 10px; }
.gallery-thumb { width: 96px; height: 68px; border-radius: 8px; border: 1px solid #e5ebf6; background: #f4f7fc; color: #9aaccc; display: flex; align-items: center; justify-content: center; font-size: 12px; position: relative; overflow: hidden; }
.gallery-thumb.add { border-style: dashed; color: #2a62d9; font-size: 24px; background: #fff; }
.gallery-thumb.has-image img { width: 100%; height: 100%; object-fit: cover; }
.cover-preview { width: 160px; height: 100px; border: 1px solid #e5ebf6; border-radius: 8px; overflow: hidden; position: relative; }
.cover-preview img { width: 100%; height: 100%; object-fit: cover; border: 0; outline: none; }
.thumb-remove { position: absolute; top: 4px; right: 4px; width: 20px; height: 20px; border-radius: 50%; border: 1px solid #f2bcbc; background: #fff; color: #c62828; line-height: 16px; font-size: 14px; cursor: pointer; }
.upload-error { color: #c62828; font-size: 12px; }
.required { color: #c62828; }
.lang { color: #2962ff; }
.actions-card { margin-bottom: 0; }
label { font-size: 12px; color: #64748b; font-weight: 600; display: block; margin-bottom: 6px; }
.hint { font-size: 12px; color: #91a2c3; margin-top: 6px; }

@media (max-width: 1100px) {
  .pricing-row { grid-template-columns: 1fr; }
  .amenity-grid { grid-template-columns: repeat(2, 1fr); }
}
</style>
